import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { refreshAccessToken } from './authService';

const apiClient: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL ?? '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});



function clearAuthCookies(): void {
  Cookies.remove('authToken');
  Cookies.remove('refreshToken');
  Cookies.remove('userRole');
  Cookies.remove('email');
}

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = Cookies.get('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Don't intercept 401 errors from auth endpoints (login, refresh, etc.)
    const isAuthEndpoint = config?.url?.includes('/auth/login') || 
                          config?.url?.includes('/auth/refresh') ||
                          config?.url?.includes('/auth/logout');

    if (status === 401 && !config._retry && !isAuthEndpoint) {
      config._retry = true;
      const refreshToken = Cookies.get('refreshToken');
      if (refreshToken) {
        try {
          const newAccessToken = await refreshAccessToken();
          Cookies.set('authToken', newAccessToken, { expires: 1 });
          if (config.headers) {
            config.headers.Authorization = `Bearer ${newAccessToken}`;
          }
          return apiClient.request(config);
        } catch (refreshErr) {
          clearAuthCookies();
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          return Promise.reject(refreshErr);
        }
      }
      clearAuthCookies();
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      return Promise.reject(error);
    } else if (status === 401 && !isAuthEndpoint) {
      clearAuthCookies();
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    } else if (status === 403) {
      console.error('Access forbidden');
    } else if (status === 404) {
      console.error('Resource not found');
    } else if (status === 500) {
      console.error('Server error');
    } else if (!isAuthEndpoint) {
      console.error('API Error:', error.response?.data || error.message);
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// Helper function to get auth headers (for external use)
export const getAuthHeaders = () => {
  const token = Cookies.get('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const isAuthenticated = (): boolean => {
  return !!Cookies.get('authToken');
};

/** Unwrap backend response { success, data } to payload; throw on success false or non-2xx. */
export function unwrapResponse<T>(response: { data: { success?: boolean; data?: T; message?: string } }): T {
  const body = response.data;
  if (body?.success === false) {
    throw new Error((body as { message?: string }).message ?? 'Request failed');
  }
  return (body?.data ?? body) as T;
}
